import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccountNumber } from '../lib/auth.js';
import { getTerminals, getServiceLines } from '../lib/starlink-api.js';

/**
 * CommandPalette — Linear / Stripe-style ⌘K global search.
 * Searches terminals, service lines, sites, and navigation routes.
 * Opens via the search icon in TopBar or ⌘K / Ctrl+K.
 */
const NAV_ROUTES = [
  { type: 'route', label: 'Dashboard',             to: '/' },
  { type: 'route', label: 'Network map',           to: '/network' },
  { type: 'route', label: 'Terminals',             to: '/terminals' },
  { type: 'route', label: 'Service lines',         to: '/service-lines' },
  { type: 'route', label: 'Groups & tags',         to: '/groups' },
  { type: 'route', label: 'Alerts',                to: '/alerts' },
  { type: 'route', label: 'Alert rules',           to: '/thresholds' },
  { type: 'route', label: 'SLA performance',       to: '/sla' },
  { type: 'route', label: 'Trends & reports',      to: '/trends' },
  { type: 'route', label: 'Firmware',              to: '/firmware' },
  { type: 'route', label: 'Audit log',             to: '/audit' },
  { type: 'route', label: 'Billing',               to: '/billing' },
  { type: 'route', label: 'Reports',               to: '/reports' },
  { type: 'route', label: 'Service requests',      to: '/requests' },
  { type: 'route', label: 'New service request',   to: '/requests/new' },
  { type: 'route', label: 'Help & support',        to: '/support' },
];

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIdx(0);
    setTimeout(() => inputRef.current?.focus(), 50);
    async function load() {
      const acc = getAccountNumber();
      const [ts, sls] = await Promise.all([getTerminals(acc), getServiceLines(acc)]);
      setTerminals(ts);
      setServiceLines(sls);
    }
    load();
  }, [open]);

  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      // Empty query — show top routes
      return NAV_ROUTES.slice(0, 8);
    }
    const r = [];
    NAV_ROUTES.forEach((route) => {
      if (route.label.toLowerCase().includes(q)) r.push(route);
    });
    terminals.forEach((t) => {
      const haystack = `${t.siteName || ''} ${t.model || ''} ${t.deviceId || ''} ${t.city || ''} ${t.addressDescription || ''}`.toLowerCase();
      if (haystack.includes(q)) {
        r.push({
          type: 'terminal',
          label: t.siteName || t.model,
          sub: `${t.model} · ${t.addressDescription}`,
          to: `/terminals/${t.deviceId}`,
        });
      }
    });
    serviceLines.forEach((sl) => {
      const haystack = `${sl.nickname || ''} ${sl.serviceLineNumber || ''} ${sl.planName || ''}`.toLowerCase();
      if (haystack.includes(q)) {
        r.push({
          type: 'service-line',
          label: sl.nickname,
          sub: `Service line · ${sl.planName} · ${sl.serviceLineNumber}`,
          to: `/service-lines`,
        });
      }
    });
    return r.slice(0, 14);
  }, [query, terminals, serviceLines]);

  useEffect(() => { setSelectedIdx(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, results.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     {
        const hit = results[selectedIdx];
        if (hit?.to) { navigate(hit.to); onClose(); }
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, selectedIdx, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-xl border border-[var(--color-line-1)] shadow-[var(--shadow-elevated)] w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-line-soft)]">
          <svg className="w-4 h-4 text-[var(--color-ink-4)] flex-shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="9" r="6" />
            <path d="m14 14 4 4" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search terminals, sites, alerts, settings…"
            className="flex-1 bg-transparent border-0 px-0 py-0 text-base text-[var(--color-ink-1)] placeholder-[var(--color-ink-5)] focus:outline-none focus:ring-0 focus:shadow-none"
            style={{ boxShadow: 'none' }}
          />
          <kbd className="text-[10px] text-[var(--color-ink-4)] mono bg-[var(--color-bg-soft)] px-1.5 py-0.5 rounded border border-[var(--color-line-1)]">ESC</kbd>
        </div>
        <ul className="max-h-[400px] overflow-y-auto">
          {results.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-[var(--color-ink-4)]">No results for "{query}"</li>
          )}
          {results.map((r, i) => (
            <li
              key={`${r.type}-${r.to}-${i}`}
              onClick={() => { navigate(r.to); onClose(); }}
              onMouseEnter={() => setSelectedIdx(i)}
              className={`px-4 py-2.5 cursor-pointer flex items-center justify-between gap-3 ${selectedIdx === i ? 'bg-[var(--color-brand-50)]' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[var(--color-ink-1)] text-sm">{r.label}</div>
                {r.sub && <div className="text-xs text-[var(--color-ink-4)] truncate mt-0.5">{r.sub}</div>}
              </div>
              <TypeBadge type={r.type} />
            </li>
          ))}
        </ul>
        <div className="border-t border-[var(--color-line-soft)] px-4 py-2 flex items-center justify-between text-[10px] text-[var(--color-ink-4)]">
          <div className="flex items-center gap-3">
            <span><kbd className="mono bg-[var(--color-bg-soft)] px-1 rounded">↑↓</kbd> Navigate</span>
            <span><kbd className="mono bg-[var(--color-bg-soft)] px-1 rounded">↵</kbd> Select</span>
          </div>
          <span>{results.length} result{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const labels = {
    route: 'Page',
    terminal: 'Terminal',
    'service-line': 'Service line',
  };
  return (
    <span className="text-[10px] uppercase tracking-wider text-[var(--color-ink-4)] bg-[var(--color-bg-soft)] px-1.5 py-0.5 rounded border border-[var(--color-line-1)] flex-shrink-0">
      {labels[type] || type}
    </span>
  );
}
